import classNames from 'classnames';
import * as _ from 'lodash-es';
import React, { useRef } from 'react';
import { flushSync } from 'react-dom';
import { useMutation } from '@apollo/client';
import { useRightPanel } from '../app/layout/RightPanel';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import type { BannerEditorRef } from '../components/BannerEditor';
import BannerEditor from '../components/BannerEditor';
import Button from '../components/Button';
import InPlaceEditControls from '../components/InPlaceEditControls';
import InPlaceEditable from '../components/InPlaceEditable';
import { MenuItem, MenuSeparator } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import SummaryCollapsibleContent from '../components/SummaryCollapsibleContent';
import Tooltip from '../components/Tooltip';
import {
  BEACON_TOPIC_BANNER,
  BEACON_TOPIC_EDIT,
  BEACON_TOPIC_OVERFLOW_MENU,
} from '../constants/onboarding';
import RichTextDescriptionEditor from '../editor/RichTextDescriptionEditor';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { GetTopicUsers_TopicFragment } from '../graphql/generated/graphql';
import {
  TopicPostsType,
  TopicPrivacy,
  UserType,
} from '../graphql/generated/graphql';
import UPDATE_TOPIC_MUTATION from '../graphql/updateTopicMutation';
import addIcon from '../icons/non-prefilled/16x16/add.svg';
import clockIcon from '../icons/non-prefilled/16x16/clock.svg';
import copyIcon from '../icons/non-prefilled/16x16/copy.svg';
import editIcon from '../icons/non-prefilled/16x16/editPost.svg';
import memberIcon from '../icons/non-prefilled/16x16/member.svg';
import memberRemoveIcon from '../icons/non-prefilled/16x16/memberRemove.svg';
import reorderIcon from '../icons/non-prefilled/16x16/reorder.svg';
import settingsIcon from '../icons/non-prefilled/16x16/settings.svg';
import subtopicsIcon from '../icons/non-prefilled/16x16/subtopics.svg';
import topicIcon from '../icons/non-prefilled/16x16/topic.svg';
import trashIcon from '../icons/non-prefilled/16x16/trash.svg';
import TutorialBeacon from '../onboarding/tutorial/TutorialBeacon';
import SidebarMenuItem from '../post/topbar/SidebarMenuItem';
import useIsLayoutSize from '../post/utils/useIsLayoutSize';
import { LayoutSize } from '../post/utils/useLayoutSize';
import ReorderTopicsModal from '../sidebar/ReorderTopicsModal';
import { topicToasts } from '../toasts/toasts';
import { UIActions } from '../ui/uiSlice';
import AdminBadge from '../user/AdminBadge';
import { useAllUsersMap } from '../user/useAllUsers';
import populateObjects from '../utils/populateObjects';
import fullOrgUrl from '../utils/url/fullOrgUrl';
import topicPath, { publicTopicPath } from '../utils/url/topicPath';
import useBeforeRouterLeaveConfirmWithModal from '../utils/useBeforeRouterLeaveConfirmWithModal';
import usePublicContentPermissions from '../utils/usePublicContentPermissions';
import TopicActionButtons from './TopicActionButtons';
import TopicActivitiesPanel from './TopicActivitiesPanel';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import TopicNameWithIcon from './TopicNameWithIcon';
import type { EditTopicPayload, EditTopicState } from './TopicView';
import * as styles from './TopicViewHeader.module.scss';
import { TOPIC_SETTING_MENU_TITLE } from './constants';
import EditTopicModal from './modal/EditTopicModal';
import NewTopicModal from './modal/NewTopicModal';
import topicPolicyCheck, {
  MEMBER_EDITABLE_ALL,
  TopicPolicyAction,
  isRequestor,
  isUserDirectlyInTopic,
  isUserInTopic,
} from './topicPolicyCheck';
import { useAllTopicMap } from './useAllTopics';
import useDeleteTopic from './useDeleteTopic';
import {
  useAddTopicUser,
  useRemoveTopicUser,
  useRequestAccess,
} from './useTopicUserMutations';
import TopicUsersPanel from './users/TopicUsersPanel';
import type { UserTopicAccessSource } from './utils/getTopicUsers';
import getTopicUsers, {
  TopicAccessSource,
  getTopicAccessSource,
} from './utils/getTopicUsers';

const TOPIC_FRAGMENT = gql(`
  fragment TopicViewHeader_topic on Topic {
    id
    name
    privacy
    memberEditable
    description
    postCounts {
      type
      count
    }
    ancestors {
      id
    }
    banner {
      original
    }
    children {
      id
    }
    descendants(first: 0) {
      totalCount
    }
    ...topicPath_topic
    ...EditTopicModal_topic
    ...useAddTopicUser_topic
    ...isRequestor_topic
  }
`);

const getLeaveDisabledTooltip = (
  userAccessSource: UserTopicAccessSource,
): string | undefined => {
  let role: 'owner' | 'member' | undefined;
  let source: TopicAccessSource | undefined;

  (['member', 'owner'] as const).forEach((r) => {
    [TopicAccessSource.Inherited, TopicAccessSource.Group].forEach((s) => {
      if (userAccessSource[r][s]) {
        role = r;
        source = s;
      }
    });
  });

  const roleText = role === 'owner' ? 'an owner' : 'a member';
  if (source === TopicAccessSource.Group) {
    return `You are in a group\nthat is ${roleText}\nof this topic`;
  }

  if (source === TopicAccessSource.Inherited) {
    return `You are ${roleText}\nof this topic through\nthe parent topic`;
  }

  return undefined;
};

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const useTopicViewHeaderMenuItems = ({
  canJoin,
  canEdit,
  canDelete,
  hasJoined,
  hasDirectlyJoined,
  canAddSubtopic,
  onJoin,
  onLeave,
  topic,
  topicForPermissionCheck,
  onEditDetailsClick,
}: {
  canJoin: boolean;
  canEdit: boolean;
  canDelete: boolean;
  hasJoined: boolean;
  hasDirectlyJoined: boolean;
  canAddSubtopic: boolean;
  onJoin: () => unknown;
  onLeave: () => unknown;
  topic: TopicViewHeaderProps['topic'];
  topicForPermissionCheck: GetTopicUsers_TopicFragment | null;
  onEditDetailsClick: () => void;
}) => {
  const { isPermittedToCreatePublicSubtopic } = usePublicContentPermissions();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const deleteTopic = useDeleteTopic(topic.id);
  const topicMap = useAllTopicMap();
  const children = _.compact(topic.children.map((child) => topicMap[child.id]));

  const isMobile = useIsLayoutSize(LayoutSize.mobile);
  const { openRightPanel } = useRightPanel();

  const requestAccess = useRequestAccess();

  const handleModifyClick = () => {
    dispatch(UIActions.openModal(EditTopicModal, { topic }));
  };

  const handlePublicLinkCopy = async () => {
    await navigator.clipboard.writeText(fullOrgUrl(publicTopicPath(topic)));
    dispatch(UIActions.pushToast(topicToasts.publicTopicLinkCopied));
  };

  const handleCreateSubtopic = () =>
    dispatch(UIActions.openModal(NewTopicModal, { parentId: topic.id }));

  const { memberEditable } = topic;

  const canRequest = !hasJoined && !canJoin;
  const hasRequested = canRequest && isRequestor(topic, user);

  const accessSource = topicForPermissionCheck
    ? getTopicAccessSource(topicForPermissionCheck, user.id)
    : null;

  const canLeave =
    hasJoined &&
    accessSource &&
    (accessSource.owner[TopicAccessSource.Direct] ||
      accessSource.member[TopicAccessSource.Direct]);

  const leaveDisabledTooltip =
    hasJoined && !canLeave && accessSource
      ? getLeaveDisabledTooltip(accessSource)
      : undefined;

  const descendantsCount = topic.descendants?.totalCount ?? 0;

  const publishedPostsCount =
    topic.postCounts.find((count) => count.type === TopicPostsType.Published)
      ?.count ?? 0;

  const { owners, members } = topicForPermissionCheck
    ? getTopicUsers(topicForPermissionCheck)
    : { owners: [], members: [] };

  const whoCanEdit = `Only topic ${
    memberEditable === MEMBER_EDITABLE_ALL ? 'members' : 'owners'
  }`;
  const editDisabledTip = canEdit
    ? undefined
    : `${whoCanEdit}\ncan edit this topic`;
  const deleteDisabledTip = canDelete
    ? undefined
    : `${whoCanEdit}\ncan delete this topic`;

  const isSubtopicPermitted =
    isPermittedToCreatePublicSubtopic || topic.privacy !== TopicPrivacy.Public;

  return [
    canJoin && (
      <MenuItem key="join" icon={addIcon} onClick={onJoin}>
        Join topic
      </MenuItem>
    ),
    canRequest && (
      <MenuItem
        key="request"
        icon={addIcon}
        disabled={hasRequested}
        onClick={async () => {
          await requestAccess(topic.id);
          dispatch(UIActions.pushToast(topicToasts.membershipRequested));
        }}
        tooltip={
          hasRequested
            ? 'Membership requested.\nYou will receive an email\nwhen access is granted.'
            : undefined
        }
      >
        Request to join
      </MenuItem>
    ),
    topic.privacy === 'PUBLIC' && (
      <MenuItem key="copyLink" icon={copyIcon} onClick={handlePublicLinkCopy}>
        Copy public link
      </MenuItem>
    ),
    !isMobile && (
      <TutorialBeacon key="banner" id={BEACON_TOPIC_BANNER} placement="left">
        <MenuItem
          icon={editIcon}
          onClick={onEditDetailsClick}
          tooltip={editDisabledTip}
          disabled={!canEdit}
        >
          Edit appearance
        </MenuItem>
      </TutorialBeacon>
    ),
    <TutorialBeacon key="edit" id={BEACON_TOPIC_EDIT} placement="left">
      <MenuItem
        icon={settingsIcon}
        onClick={handleModifyClick}
        tooltip={editDisabledTip}
        disabled={!canEdit}
      >
        {TOPIC_SETTING_MENU_TITLE}
      </MenuItem>
    </TutorialBeacon>,
    canAddSubtopic && (
      <MenuItem
        key="subtopic"
        disabled={!isSubtopicPermitted}
        tooltip={
          isSubtopicPermitted
            ? undefined
            : 'Only admins can\ncreate subtopics\nof public topic'
        }
        icon={topicIcon}
        onClick={handleCreateSubtopic}
      >
        Create subtopic
      </MenuItem>
    ),
    canEdit && children.length > 1 && (
      <MenuItem
        key="reorder"
        icon={reorderIcon}
        onClick={() => {
          dispatch(
            UIActions.openModal(ReorderTopicsModal, {
              topics: children,
              parentTopic: {
                ...topic,
                ancestors: _.compact(
                  topic.ancestors.map((ancestor) => topicMap[ancestor.id]),
                ),
              },
            }),
          );
        }}
      >
        Reorder subtopics
      </MenuItem>
    ),
    <SidebarMenuItem key="sidebarItem" item={topic} />,
    hasJoined && (
      <MenuItem
        key="leave"
        disabled={!!leaveDisabledTooltip}
        icon={memberRemoveIcon}
        onClick={onLeave}
        tooltip={leaveDisabledTooltip}
      >
        Leave topic
      </MenuItem>
    ),
    <MenuSeparator key="actionsSeparator" />,
    <MenuItem
      key="activity"
      icon={clockIcon}
      onClick={() => {
        openRightPanel(<TopicActivitiesPanel />);
      }}
    >
      Activity
    </MenuItem>,
    <MenuItem
      key="members"
      icon={memberIcon}
      onClick={() => {
        openRightPanel(<TopicUsersPanel />);
      }}
      disabled={members.length === 0 && owners.length === 0}
    >
      Members
    </MenuItem>,
    !isMobile && (
      <MenuItem
        key="contentMap"
        icon={subtopicsIcon}
        to={topicPath(topic, 'subtopics')}
        disabled={descendantsCount === 0 && publishedPostsCount === 0}
      >
        Content Map
      </MenuItem>
    ),
    <MenuSeparator key="separator" />,
    !hasDirectlyJoined && !canJoin && user.type === UserType.Admin && (
      <MenuItem
        key="joinAsAdmin"
        icon={addIcon}
        onClick={onJoin}
        right={<AdminBadge />}
      >
        Join topic
      </MenuItem>
    ),
    <MenuItem
      key="delete"
      onClick={() => {
        deleteTopic();
      }}
      danger
      icon={trashIcon}
      disabled={!canDelete}
      tooltip={deleteDisabledTip}
    >
      Delete topic
    </MenuItem>,
  ];
};

interface TopicViewHeaderProps {
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  editing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  editTopicState: (EditTopicState & { touched: boolean }) | null;
  updateEditTopicState: (payload: Partial<EditTopicState | null>) => void;
  resetEditTopicState: (t: EditTopicPayload) => void;
}

const TopicViewHeader = ({
  topic,
  editing,
  setIsEditing,
  editTopicState,
  updateEditTopicState,
  resetEditTopicState,
}: TopicViewHeaderProps) => {
  const topicMap = useAllTopicMap();
  const usersMap = useAllUsersMap();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.session.user);
  const isDescriptionCollapsed = useAppSelector(
    (state) => !!state.ui.descriptionCollapsedTopics[topic.id],
  );
  const bannerEditor = useRef<BannerEditorRef>(null);

  const { banner } = topic;
  const ancestors = _.compact(
    topic.ancestors.map((ancestor) => topicMap[ancestor.id]),
  );
  const currentBannerUrl = banner?.original;

  const [updateTopic, { loading: isSaving }] = useMutation(
    UPDATE_TOPIC_MUTATION,
  );

  const saveTopic = async () => {
    if (!editTopicState) return;

    const bannerFile = await bannerEditor.current?.getBannerFile();
    const nextBanner =
      editTopicState.banner?.image !== currentBannerUrl
        ? bannerFile
        : undefined;

    const { data } = await updateTopic({
      variables: {
        id: topic.id,
        name: editTopicState.name,
        description: JSON.stringify(editTopicState.description),
        banner: nextBanner,
        unsplashId: editTopicState.banner?.unsplashId,
        defaultSort: editTopicState.defaultSort,
        defaultShow: editTopicState.defaultShow,
      },
    });

    if (data?.updateTopic) {
      // we need to use flush sync here because otherwise the state update will be batched
      // with the redirect happening via setIsEditing(false), but we need to make sure that
      // the state is resetted before the navigation happens so that the confirm modal from
      // useBeforeRouterLeaveConfirmWithModal will not show up
      flushSync(() => {
        resetEditTopicState(data.updateTopic);
      });
    }
  };

  useBeforeRouterLeaveConfirmWithModal({
    skip: !editing || !editTopicState?.touched,
    onConfirm: saveTopic,
    title: 'Save changes?',
    message: "Please save your changes if you'd like to keep them.",
    confirmButtonText: 'Save',
    cancelButtonText: 'Discard',
  });

  const handleSave = async () => {
    await saveTopic();
    setIsEditing(false);
  };

  const handleCancel = () => setIsEditing(false);

  const toggleDescription = () => {
    dispatch(UIActions.toggleTopicDescription(topic.id));
  };

  const topicForPermissionCheck = topicMap[topic.id] ?? null;

  const removeTopicUser = useRemoveTopicUser();
  const addTopicUser = useAddTopicUser();
  const handleJoin = () =>
    topicForPermissionCheck && addTopicUser(topicForPermissionCheck, user);
  const handleLeave = () =>
    topicForPermissionCheck && removeTopicUser(topicForPermissionCheck, user);

  const hasJoined =
    !!topicForPermissionCheck && isUserInTopic(topicForPermissionCheck, user);

  const hasDirectlyJoined =
    !!topicForPermissionCheck &&
    isUserDirectlyInTopic(topicForPermissionCheck, user);

  const canAddOrRemoveMember =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.AddRemoveMember,
      topicForPermissionCheck,
      user,
    );
  const canEdit =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.EditTopic,
      topicForPermissionCheck,
      user,
    );
  const canDelete =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.DeleteTopic,
      topicForPermissionCheck,
      user,
    );
  const canAddSubtopic =
    !!topicForPermissionCheck &&
    topicPolicyCheck(
      TopicPolicyAction.AddSubtopic,
      topicForPermissionCheck,
      user,
    );

  const canJoin = !hasJoined && canAddOrRemoveMember;

  const { owners, members } = topicForPermissionCheck
    ? getTopicUsers(topicForPermissionCheck)
    : { owners: [], members: [] };

  const topicUsers = populateObjects(usersMap, [...owners, ...members]);

  const menuItems = useTopicViewHeaderMenuItems({
    hasJoined,
    hasDirectlyJoined,
    canJoin,
    canEdit,
    canDelete,
    canAddSubtopic,
    onJoin: handleJoin,
    onLeave: handleLeave,
    topic,
    topicForPermissionCheck,
    onEditDetailsClick: () => setIsEditing(true),
  });

  const controls = (
    <div className={styles.controls}>
      {editing ? (
        <InPlaceEditControls
          saving={isSaving}
          savingDisabled={editTopicState?.name === ''}
          onCancelClick={handleCancel}
          onSaveClick={handleSave}
        />
      ) : (
        <>
          <TopicActionButtons topicUsers={topicUsers} limit={canJoin ? 4 : 5} />
          <div className={styles.overflownControls}>
            <OverflowMenu
              beaconId={BEACON_TOPIC_OVERFLOW_MENU}
              beaconPlacement="top"
              applyNegativeMargin={false}
            >
              {menuItems}
            </OverflowMenu>
          </div>
          {canJoin && (
            <Tooltip
              placement="left"
              content={
                'Become a member to receive\nnotifications for new posts\nand edits in this topic.'
              }
            >
              <Button variant="primary" onClick={handleJoin}>
                Join topic
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </div>
  );

  const isShowingBanner = currentBannerUrl != null || editing;

  const bannerEditorState = (() => {
    if (isSaving) return 'saving';
    return editing ? 'editing' : 'viewing';
  })();

  return (
    <div>
      {isShowingBanner && (
        <BannerEditor
          ref={bannerEditor}
          currentBannerUrl={currentBannerUrl}
          state={bannerEditorState}
          onBannerChange={(nextBanner) =>
            updateEditTopicState({ banner: nextBanner })
          }
          banner={editTopicState?.banner ?? null}
        />
      )}
      <div
        className={classNames(styles.container, {
          [styles.hasBanner]: isShowingBanner,
        })}
      >
        <TwoColumnRow
          main={(isAsideShown) => (
            <>
              {ancestors && ancestors.length > 0 && (
                <div className={styles.ancestorsContainer}>
                  <TopicBreadcrumbs
                    topics={ancestors}
                    link={!editing}
                    showLastCaret
                  />
                </div>
              )}
              <h1>
                {editing ? (
                  <InPlaceEditable
                    defaultValue={topic.name}
                    onChange={(nextName) =>
                      updateEditTopicState({ name: nextName })
                    }
                    placeholder="Enter a Topic Name"
                    autoFocus
                  />
                ) : (
                  <TopicNameWithIcon
                    topic={topic}
                    iconSize="large"
                    showDefaultIcon
                    tooltip
                  />
                )}
              </h1>
              <div
                className={classNames(styles.description, {
                  [styles.isEditing]: editing,
                })}
              >
                {editing ? (
                  <RichTextDescriptionEditor
                    readOnly={false}
                    noTitleLine
                    namespace="topicUserDescription"
                    defaultValue={topic.description}
                    onTextChange={(nextDescription) =>
                      updateEditTopicState({ description: nextDescription })
                    }
                    contentPlaceholder="Write a description right here..."
                    testId="topicDescriptionEditor"
                  />
                ) : (
                  <SummaryCollapsibleContent
                    collapsed={isDescriptionCollapsed}
                    onCollapsedChange={toggleDescription}
                    content={topic.description}
                    maxLines={6}
                    typography="postWithNoTitleLine"
                  />
                )}
                {!isAsideShown && controls}
              </div>
            </>
          )}
          aside={controls}
        />
      </div>
    </div>
  );
};

export default TopicViewHeader;
